import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ITabletReadingSession } from '../../Interfaces/ITabletReadingSession';
import { customFetcher } from '../useFetch';

const SESSIONS_KEY = ['tabletreadingsession'];

const sessionsForTransactionKey = (idLegalTransaction: number) => [
  ...SESSIONS_KEY,
  { idLegalTransaction },
];

const POLL_INTERVAL_MS = 3000;

export const useGetActiveReadingSessions = (
  idLegalTransaction: number | null | undefined
) => {
  const enabled = idLegalTransaction != null;
  const {
    data: sessions,
    isLoading: isLoadingReadingSessions,
    refetch: refetchReadingSessions,
  } = useQuery<ITabletReadingSession[]>({
    queryKey: sessionsForTransactionKey(idLegalTransaction ?? 0),
    queryFn: () =>
      customFetcher({
        endpoint: `/tabletreadingsession?idLegalTransaction=${idLegalTransaction}&status=active`,
      }),
    enabled,
    refetchInterval: enabled ? POLL_INTERVAL_MS : false,
  });
  return { sessions, isLoadingReadingSessions, refetchReadingSessions };
};

export const useCreateReadingSession = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: createReadingSession,
    isPending: isPendingCreateReadingSession,
  } = useMutation({
    mutationFn: (body: {
      idLegalTransaction: number;
      idDocument: number;
      idTabletDevices: number[];
      ControlMode?: 'push' | 'free';
    }) =>
      customFetcher({
        endpoint: '/tabletreadingsession',
        method: 'POST',
        body,
      }) as Promise<ITabletReadingSession>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
  return { createReadingSession, isPendingCreateReadingSession };
};

export const useUpdateReadingSession = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: updateReadingSession,
    isPending: isPendingUpdateReadingSession,
  } = useMutation({
    mutationFn: ({
      idTabletReadingSession,
      body,
    }: {
      idTabletReadingSession: number;
      body: { CurrentPage?: number; ControlMode?: 'push' | 'free' };
    }) =>
      customFetcher({
        endpoint: `/tabletreadingsession/${idTabletReadingSession}`,
        method: 'PUT',
        body,
      }) as Promise<ITabletReadingSession>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
    },
  });
  return { updateReadingSession, isPendingUpdateReadingSession };
};

export interface ITabletReadingAnnotation {
  idTabletReadingAnnotation: number;
  // Achtung: ein FK, der in den `Relations` der Modul-Config steht, wird vom
  // SQL-Generator durch den PK-Namen der verknüpften Tabelle ersetzt. Hier
  // betrifft das TabletDevice und LegalClient — es kommen `idTabletDevice`
  // und `idLegalClient` an. `TabletReadingSession_...` steht in keiner
  // Relation und behält seinen Namen.
  TabletReadingSession_idTabletReadingSession?: number;
  idTabletDevice?: number | null;
  idLegalClient?: number | null;
  TabletDevice_idTabletDevice?: number;
  LegalClient_idLegalClient?: number | null;
  Page: number;
  Text: string;
  Created_at?: string | null;
  TabletName?: string | null;
  LegalClientFirstName?: string | null;
  LegalClientLastName?: string | null;
}

const annotationsKey = (idTabletReadingSession: number) => [
  'tabletreadingsession',
  idTabletReadingSession,
  'annotation',
];

export const useGetReadingAnnotations = (
  idTabletReadingSession: number | null | undefined
) => {
  const enabled = idTabletReadingSession != null;
  const {
    data: annotations,
    isLoading: isLoadingAnnotations,
    refetch: refetchAnnotations,
  } = useQuery<ITabletReadingAnnotation[]>({
    queryKey: annotationsKey(idTabletReadingSession ?? 0),
    queryFn: () =>
      customFetcher({
        endpoint: `/tabletreadingsession/${idTabletReadingSession}/annotation`,
      }),
    enabled,
    refetchInterval: enabled ? 4000 : false,
  });
  return { annotations, isLoadingAnnotations, refetchAnnotations };
};

export const useEndReadingSession = () => {
  const queryClient = useQueryClient();
  const {
    mutateAsync: endReadingSession,
    isPending: isPendingEndReadingSession,
  } = useMutation({
    mutationFn: (idTabletReadingSession: number) =>
      customFetcher({
        endpoint: `/tabletreadingsession/${idTabletReadingSession}`,
        method: 'DELETE',
      }) as Promise<{ transferredAnnotations?: number }>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_KEY });
      queryClient.invalidateQueries({ queryKey: ['draftMessage'] });
      queryClient.invalidateQueries({ queryKey: ['legalTransaction'] });
    },
  });
  return { endReadingSession, isPendingEndReadingSession };
};
